// File: src/components/layout/user-menu.tsx
// Purpose: User dropdown menu with avatar and options for InstaDev
// InstaDev - User Menu Component


import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { logout } from '@/store/slices/authSlice'

import { useSession, signOut } from '@/lib/auth/mock-auth'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'
import {
  User,
  Settings,
  CreditCard,
  Users,
  HelpCircle,
  LogOut,
  Shield,
  Bookmark,
  Activity,
} from 'lucide-react'

interface UserMenuProps {
  className?: string
}


import { UserRole, getRoleDisplayName, getRoleBadgeColor } from '@/lib/rbac/roles'
import { canAccessAdminFeatures, canAccessBilling } from '@/lib/auth/route-protection'
import { RootState } from '@/store'

export function UserMenu({ className }: UserMenuProps) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)

  // Get user data from Redux state
  const userId = useSelector((state: RootState) => state.auth.userId)
  const userType = useSelector((state: RootState) => state.auth.userType)
  const userName = useSelector((state: RootState) => state.auth.userName) || 'User'
  const userEmail = useSelector((state: RootState) => state.auth.email) || ''
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)

  // Fallback to session if Redux state is not available (for compatibility)
  const sessionRes: any = useSession() || {}
  const session = sessionRes.data

  // Use Redux state if available, otherwise fallback to session
  const displayName = userName || session?.user?.name || 'User'
  const displayEmail = userEmail || session?.user?.email || ''

  // Map userType to UserRole for compatibility with existing role system
  const userRole = userType === 'admin' || userType === 'ADMIN' ? UserRole.SUPER_ADMIN : UserRole.DEVELOPER

  const handleMenuItemClick = (path: string) => {
    setIsOpen(false) // Close dropdown before navigation
    navigate(path)
  }

  const handleLogout = () => {
    // Close dropdown first
    setIsOpen(false)

    // Clear Redux state
    dispatch(logout())

    // Clear all localStorage items
    localStorage.clear()

    // Clear session storage as well
    sessionStorage.clear()

    // Navigate to login page
    navigate('/login')
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn('relative h-9 w-9 rounded-full', className)}
        >
          <Avatar className="h-9 w-9">
            <div className="h-9 w-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
              {displayName.charAt(0).toUpperCase()}
            </div>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56" align="end" forceMount>
        {/* User Info Section */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              </Avatar>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none capitalize">
                  {displayName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {displayEmail}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`text-xs ${getRoleBadgeColor(userRole)}`}>
                {getRoleDisplayName(userRole)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                Pro
              </Badge>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Account Section */}
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => handleMenuItemClick('/staff/profile')}>
            <User className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          {/* <DropdownMenuItem onClick={() => handleMenuItemClick('/settings')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleMenuItemClick('/activity')}>
            <Activity className="mr-2 h-4 w-4" />
            <span>Activity</span>
          </DropdownMenuItem> */}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        {/* Workspace Section */}
        {/* <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => handleMenuItemClick('/team')}>
            <Users className="mr-2 h-4 w-4" />
            <span>Team</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleMenuItemClick('/bookmarks')}>
            <Bookmark className="mr-2 h-4 w-4" />
            <span>Bookmarks</span>
          </DropdownMenuItem>
          {canAccessAdminFeatures(userRole) && (
            <DropdownMenuItem onClick={() => handleMenuItemClick('/admin')}>
              <Shield className="mr-2 h-4 w-4" />
              <span>Admin Panel</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup> */}

        {/* <DropdownMenuSeparator /> */}

        {/* Billing Section */}
        {/* {canAccessBilling(userRole) && (
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => handleMenuItemClick('/billing')}>
              <CreditCard className="mr-2 h-4 w-4" />
              <span>Billing</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        )} */}

        {/* <DropdownMenuSeparator /> */}

        {/* Support Section */}
        {/* <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => handleMenuItemClick('/help')}>
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>Help & Support</span>
          </DropdownMenuItem>
        </DropdownMenuGroup> */}

        {/* <DropdownMenuSeparator /> */}

        {/* Logout */}
        <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}